/**
 * Deterministic pseudo-random number generation.
 *
 * Nothing in this package may call Math.random(). The client and server both
 * generate the world from a shared seed and must agree exactly, so every
 * random decision has to be reproducible.
 */

/** mulberry32 — small, fast, good enough distribution for world generation. */
export class Rng {
  private state: number;

  constructor(seed: number) {
    // Force to uint32. A zero state is degenerate, so nudge it.
    this.state = (seed >>> 0) || 0x9e3779b9;
  }

  /** Uniform float in [0, 1). */
  next(): number {
    this.state = (this.state + 0x6d2b79f5) >>> 0;
    let t = this.state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Uniform float in [min, max). */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /** Uniform integer in [min, max]. */
  int(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }

  bool(chance = 0.5): boolean {
    return this.next() < chance;
  }

  pick<T>(items: readonly T[]): T {
    return items[Math.floor(this.next() * items.length)]!;
  }

  /** Fisher-Yates, in place. */
  shuffle<T>(items: T[]): T[] {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      const a = items[i]!;
      items[i] = items[j]!;
      items[j] = a;
    }
    return items;
  }

  /** Normally-distributed value via Box-Muller, mean 0 stddev 1. */
  gaussian(): number {
    const u = 1 - this.next();
    const v = this.next();
    return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
  }
}

/**
 * Derive an independent sub-seed from a base seed and a string tag.
 *
 * This matters more than it looks: each generation pass (terrain, trees,
 * towers, items) gets its own stream, so adding a tree variant doesn't shift
 * every tower to a new position. Without this, tuning one system silently
 * reshuffles the entire world.
 */
export function deriveSeed(baseSeed: number, tag: string): number {
  let h = (baseSeed >>> 0) ^ 0x811c9dc5;
  for (let i = 0; i < tag.length; i++) {
    h ^= tag.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/**
 * Short, human-shareable room codes, e.g. "K7QMX2".
 *
 * The alphabet omits I, O, 0 and 1 so a code can be read aloud over voice chat
 * without ambiguity — which is exactly how these get shared in practice.
 */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no I/O/0/1
const CODE_LENGTH = 6;

/**
 * Codes are lossless only over this range: 32 symbols to the 6th power is
 * 2^30, not 2^32. Rather than lengthen every code to fit a full uint32, we
 * simply generate seeds inside the representable space — 1.07 billion distinct
 * worlds is not the limiting factor here.
 */
export const SEED_MAX = CODE_ALPHABET.length ** CODE_LENGTH; // 2^30

/** Reduce an arbitrary integer into the code-representable seed space. */
export function normalizeSeed(seed: number): number {
  return (seed >>> 0) % SEED_MAX;
}

/** A fresh random seed. The one place a non-deterministic call is correct. */
export function randomSeed(): number {
  return Math.floor(Math.random() * SEED_MAX);
}

export function seedToCode(seed: number): string {
  let n = normalizeSeed(seed);
  let out = '';
  for (let i = 0; i < CODE_LENGTH; i++) {
    out += CODE_ALPHABET[n % CODE_ALPHABET.length];
    n = Math.floor(n / CODE_ALPHABET.length);
  }
  return out;
}

export function codeToSeed(code: string): number {
  const upper = code.trim().toUpperCase();
  let n = 0;
  for (let i = upper.length - 1; i >= 0; i--) {
    const idx = CODE_ALPHABET.indexOf(upper[i]!);
    if (idx < 0) continue;
    n = n * CODE_ALPHABET.length + idx;
  }
  return normalizeSeed(n);
}

/** Whether a string is a syntactically valid room code. */
export function isValidCode(code: string): boolean {
  const upper = code.trim().toUpperCase();
  if (upper.length !== CODE_LENGTH) return false;
  for (const ch of upper) {
    if (!CODE_ALPHABET.includes(ch)) return false;
  }
  return true;
}
